/**
 * The contact-form contract.
 *
 * Imported by both the browser code and the server, so the shape of what
 * gets posted can only ever be changed in one place. If the two drift
 * apart, `npm run typecheck` fails rather than the form failing in
 * production.
 */

/** What the browser sends to `POST /api/contact`. */
export interface ContactPayload {
  name: string;
  email: string;
  organization: string;
  /** Which of the "What do you need?" pills were pressed. */
  needs: readonly string[];
  timeline: string;
  message: string;
  /**
   * Honeypot. Hidden from people, so a real submission always leaves this
   * empty; bots fill every field they find. Anything non-empty is dropped.
   */
  website: string;
}

/** A successful reply. `dryRun` means no SMTP is configured and nothing was sent. */
export interface ContactSuccess {
  ok: true;
  dryRun?: boolean;
}

/** A failed reply. `error` is safe to show to the person as-is. */
export interface ContactFailure {
  error: string;
}

export type ContactResponse = ContactSuccess | ContactFailure;

/** Field length caps, applied on both sides. */
export const LIMITS = {
  name: 120,
  email: 200,
  organization: 200,
  timeline: 80,
  message: 5000,
  need: 60,
  needs: 12,
} as const;

/**
 * Deliberately permissive: the only reliable test of an address is sending
 * to it. This catches typos and obvious rubbish, nothing more.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value);
}
